import {useEffect,useState} from "react";
import {Box,Autocomplete,TextField,Tooltip} from '@mui/material';
import {DefaultConfig,evaluateReadOnly} from '../../../core/utilities/Utilities';
const readOnlyBackgroundColor=DefaultConfig.readOnlyBackgroundColor;

// Free-text field with a suggestions dropdown: the user can pick a preset or type any value.
// Presets are the names of the LOV given in ui_suggestions_lov - deliberately not list_of_values,
// which makes the backend translate the stored value through the LOV (a typed value not in it
// would come back blank). The stored value is shown and committed as is.
const FieldAutocompleteFreeSolo=({row,fieldName,application,recordRules,readOnly,setFieldValue,setFieldChanged})=>{
    const fieldProp=row?.[fieldName];
    const fieldValue=fieldProp?.value;
    const readOnlyEvaluated=evaluateReadOnly(readOnly,recordRules,fieldProp);
    const fieldTextColor=fieldProp?.ui_text_color||null;
    const isIntegerOnly=fieldProp?.ui_type==="number"&&!!fieldProp?.integer;
    const [inputValue,setInputValue]=useState(fieldValue!=null?String(fieldValue):"");
    const [isFieldChanged,setIsFieldChanged]=useState(false);
    const lovName=fieldProp?.ui_suggestions_lov;
    // Integer-like keys enumerate in ascending numeric order, so numeric presets sort naturally.
    const options=lovName?Object.keys(application?.globalData?.list_of_values?.[lovName]||{}):[];

    useEffect(()=>{
        setIsFieldChanged(false);
        setInputValue(fieldValue!=null?String(fieldValue):"");
    },[fieldValue]);

    const sanitize=(newValue)=>{
        newValue=newValue??"";
        // Same stripping as FieldText - an integer-only DB column can't receive a decimal value.
        return isIntegerOnly?String(newValue).replace(/[^\d-]/g,'').replace(/(?!^)-/g,''):String(newValue);
    }

    const commitValue=(newValue)=>{
        setIsFieldChanged(false);
        setFieldValue(fieldName,newValue)?.then((data)=>{
            const committedValue=data?.record?.single_fields?.[fieldName]?.value;
            if(committedValue!==undefined){
                setInputValue(committedValue!=null?String(committedValue):"");
            }
        });
    }

    return (
        <Box sx={{flex:1,minWidth:0}}>
            <Autocomplete
                freeSolo
                style={{marginTop:"0px"}}
                readOnly={readOnlyEvaluated}
                fullWidth
                options={options}
                getOptionLabel={(option)=>String(option)}
                id={fieldName}
                size="small"
                value={inputValue||null}
                inputValue={inputValue}
                onInputChange={(event,newInputValue,reason)=>{
                    // 'reset' is MUI syncing the text to the selected value - not a user edit.
                    if(reason!=='input'&&reason!=='clear') return;
                    setInputValue(sanitize(newInputValue));
                    setIsFieldChanged(true);
                    setFieldChanged?.();
                }}
                onChange={(event,newValue)=>{
                    const sanitized=sanitize(newValue);
                    setInputValue(sanitized);
                    setFieldChanged?.();
                    commitValue(sanitized);
                }}
                onBlur={()=>{
                    if(isFieldChanged){
                        commitValue(inputValue);
                    }
                }}
                renderInput={(params)=>(
                    <Tooltip
                        placement="top"
                        title={fieldProp?.ui_tooltip?application.getLocale(fieldProp?.ui_tooltip):''}
                        disableHoverListener={!fieldProp?.ui_tooltip}>
                    <TextField {...params}
                        name={fieldName}
                        sx={{
                            div:{backgroundColor:readOnlyEvaluated?readOnlyBackgroundColor:""},
                            input:fieldTextColor?{color:fieldTextColor}:undefined}}
                        style={{marginTop:"0px",marginBottom:"8px"}}
                        fullWidth
                        required={fieldProp?.required?fieldProp?.required:false}
                        error={fieldProp?.validation_error?fieldProp?.validation_error:false}
                        helperText={fieldProp?.validation_error_message?fieldProp?.validation_error_message:''}
                        size="small"
                        variant="outlined"
                        label={fieldProp?.ui_label_locale?application.getLocale(fieldProp?.ui_label_locale):fieldName}
                        margin="normal"
                        inputProps={{...params.inputProps,...(isIntegerOnly?{inputMode:'numeric',pattern:'-?[0-9]*'}:{})}}
                    />
                    </Tooltip>
                )}
            />
        </Box>
    );
}

export default FieldAutocompleteFreeSolo;
